import { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, ABSENCE_TYPES } from "@/lib/format";
import { StatusBadge } from "@/components/Brand";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { ManualAbsenceDialog } from "@/components/ManualAbsenceDialog";

export const absenceSpan = (a) =>
  `${fmtDate(a.start_date)}${a.end_date !== a.start_date ? ` → ${fmtDate(a.end_date)}` : ""}${a.start_time ? ` · ${a.start_time}–${a.end_time}` : " · intera giornata"}`;
export const requestedAt = (a) => {
  if (!a.created_at) return "";
  const d = new Date(a.created_at);
  return `Richiesta il ${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })} alle ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
};

export default function AdminAbsences() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("in_attesa");
  const load = useCallback(() => api.get("/absences").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const decide = async (a, status) => {
    try {
      await api.post(`/admin/absences/${a.id}/decision`, { status });
      toast.success(status === "approvato" ? "Assenza approvata" : "Assenza rifiutata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const shown = items.filter((a) => filter === "all" || a.status === filter);

  return (
    <div>
      <PageHeader eyebrow="Gestione assenze" title="Permessi"><ManualAbsenceDialog onSaved={() => { setFilter("approvato"); load(); }} /></PageHeader>
      <Tabs value={filter} onValueChange={setFilter} className="mb-5">
        <TabsList>
          <TabsTrigger value="in_attesa" data-testid="absences-filter-pending">Da valutare</TabsTrigger>
          <TabsTrigger value="approvato" data-testid="absences-filter-approved">Approvate</TabsTrigger>
          <TabsTrigger value="rifiutato" data-testid="absences-filter-rejected">Rifiutate</TabsTrigger>
          <TabsTrigger value="all" data-testid="absences-filter-all">Tutte</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="divide-y rounded-2xl border bg-white" data-testid="absences-list">
        {shown.map((a, i) => (
          <div key={a.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`absence-row-${i}`}>
            <div>
              <p className="font-semibold">{a.technician_name} <span className="ml-2 text-sm font-normal text-slate-500">{ABSENCE_TYPES[a.type]}</span></p>
              <p className="font-mono text-xs text-slate-500">{absenceSpan(a)}</p>
              {a.created_at && <p className="text-xs text-slate-400" data-testid={`absence-requested-${i}`}>{requestedAt(a)}</p>}
              {a.reason && <p className="mt-1 text-sm text-slate-600">{a.reason}</p>}
              {a.manual && <p className="text-xs text-slate-400" data-testid={`absence-manual-${i}`}>Inserita dall'ufficio{a.created_by ? ` (${a.created_by})` : ""}</p>}
            </div>
            <div className="flex items-center gap-2">
              {a.status === "in_attesa" ? (
                <>
                  <Button size="sm" className="rounded-full" onClick={() => decide(a, "approvato")} data-testid={`absence-approve-${i}`}><Check className="h-4 w-4" /> Approva</Button>
                  <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => decide(a, "rifiutato")} data-testid={`absence-reject-${i}`}><X className="h-4 w-4" /> Rifiuta</Button>
                </>
              ) : <StatusBadge status={a.status} testid={`absence-status-${i}`} />}
            </div>
          </div>
        ))}
        {!shown.length && <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="absences-empty">Nessuna richiesta.</p>}
      </div>
    </div>
  );
}
